import { useEffect, useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router';
import { ApiError, api } from '../lib/api.js';
import { useCurrentUser } from '../lib/auth.js';
import { useRetroStore } from '../lib/retroStore.js';
import { FullPageState, Spinner } from '../components/ui.js';
import { RetroBoard } from '../components/retro/RetroBoard.js';

export function RetroPage() {
  const { retroId } = useParams<{ retroId: string }>();
  const navigate = useNavigate();
  const { data: user, isLoading: loadingUser } = useCurrentUser();

  const { snapshot, status, fatalError, connect, disconnect } = useRetroStore();
  // A closed retro refuses new seats at the join endpoint; the socket then
  // rejects the handshake as well. Recording the refusal lets the page show
  // why, instead of the generic "not in this retrospective" failure.
  const [ended, setEnded] = useState(false);
  // Set when the join call says the retro is gone — a deleted retro, or a
  // mistyped URL. Without this the socket handshake fails next and the page
  // blames the visitor's access instead.
  const [missing, setMissing] = useState(false);

  useEffect(() => {
    if (!retroId) return;

    let cancelled = false;

    // A signed-in team member arriving on a bare retro URL has no seat yet, so
    // claim one before opening the socket. Failure here is fine — they may be a
    // guest who already has a cookie, and the socket handshake decides.
    (async () => {
      if (user) {
        try {
          await api.retros.join(retroId);
        } catch (error) {
          if (error instanceof ApiError && error.code === 'CONFLICT') setEnded(true);
          if (error instanceof ApiError && error.code === 'NOT_FOUND') {
            setMissing(true);
            // No socket: the handshake can only fail, and its generic error
            // would claim the visitor is simply not in the retro.
            return;
          }
        }
      }
      if (!cancelled) connect(retroId);
    })();

    return () => {
      cancelled = true;
    };
  }, [retroId, user, connect]);

  useEffect(() => () => disconnect(), [disconnect]);

  if (!retroId) return null;

  if (loadingUser || (status === 'connecting' && !snapshot)) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label="Joining the retrospective…" />
      </div>
    );
  }

  if (missing) {
    return (
      <FullPageState title="This retrospective no longer exists">
        <p>It may have been deleted. Its comments, votes and actions are gone with it.</p>
        <div className="mt-4 flex justify-center gap-2">
          {user ? (
            <Link to="/" className="btn-ghost">
              Back to dashboard
            </Link>
          ) : (
            <button type="button" className="btn-primary" onClick={() => navigate('/login')}>
              Sign in
            </button>
          )}
        </div>
      </FullPageState>
    );
  }

  if (ended && !snapshot) {
    return (
      <FullPageState title="This retrospective has ended">
        <p>You are not on this team. People who took part — and everyone on the team — can still see the report.</p>
        <div className="mt-4 flex justify-center gap-2">
          {user ? (
            <Link to="/" className="btn-ghost">
              Back to dashboard
            </Link>
          ) : (
            <button type="button" className="btn-primary" onClick={() => navigate('/login')}>
              Sign in
            </button>
          )}
        </div>
      </FullPageState>
    );
  }

  if (status === 'error' || (fatalError && !snapshot)) {
    return (
      <FullPageState title="You’re not in this retrospective yet">
        <p>{fatalError ?? 'Ask the facilitator for an invite link.'}</p>
        <div className="mt-4 flex justify-center gap-2">
          {user ? (
            <Link to="/" className="btn-ghost">
              Back to dashboard
            </Link>
          ) : (
            <button type="button" className="btn-primary" onClick={() => navigate('/login')}>
              Sign in
            </button>
          )}
        </div>
      </FullPageState>
    );
  }

  if (!snapshot) {
    return (
      <div className="flex min-h-dvh items-center justify-center">
        <Spinner label="Loading the board…" />
      </div>
    );
  }

  return <RetroBoard snapshot={snapshot} isSignedIn={Boolean(user)} />;
}
